// src/pages/Cart.tsx - CARRITO DE COMPRAS
import React from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { formatPrice } from '../lib/utils';

const Cart: React.FC = () => {
  const { items, removeItem, updateQuantity, total, itemCount, clearCart } = useCart();

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-infinity-black flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag size={64} className="text-infinity-yellow mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-infinity-yellow mb-4">Tu carrito está vacío</h1>
          <p className="text-infinity-white/80 mb-8">Agrega algunos productos para comenzar</p>
          <Link to="/productos" className="btn-primary bg-infinity-yellow text-infinity-black hover:bg-infinity-purple hover:text-infinity-white">
            Explorar Productos
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-infinity-black">
      {/* Header */}
      <div className="bg-infinity-gradient shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold text-infinity-yellow">
            Carrito de Compras ({itemCount} {itemCount === 1 ? 'producto' : 'productos'})
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Lista de Productos */}
          <div className="lg:col-span-2">
            <div className="bg-infinity-dark rounded-lg shadow-lg overflow-hidden">
              {/* Header de la tabla */}
              <div className="bg-infinity-black px-6 py-4 border-b border-infinity-yellow">
                <div className="grid grid-cols-6 gap-4 font-semibold text-infinity-yellow">
                  <div className="col-span-2">Producto</div>
                  <div className="text-center">Precio</div>
                  <div className="text-center">Cantidad</div>
                  <div className="text-center">Subtotal</div>
                  <div className="text-center">Acciones</div>
                </div>
              </div>

              {/* Items del carrito */}
              <div className="divide-y divide-infinity-yellow/20">
                {items.map((item) => (
                  <div key={item.product.id} className="p-6">
                    <div className="grid grid-cols-6 gap-4 items-center">
                      {/* Producto */}
                      <div className="col-span-2">
                        <div className="flex items-center space-x-4">
                          <div className="w-16 h-16 bg-infinity-yellow rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-xs text-infinity-black">IMG</span>
                          </div>
                          <div>
                            <Link
                              to={`/productos/${item.product.id}`}
                              className="font-medium text-infinity-yellow hover:text-infinity-white"
                            >
                              {item.product.name}
                            </Link>
                            <p className="text-sm text-infinity-white/80">{item.product.category}</p>
                            {/* Mostrar advertencia si quedan pocas unidades */}
                            {typeof item.product.inStock === 'number' && item.product.inStock < 10 && (
                              <p className="text-xs text-infinity-yellow">Solo {item.product.inStock} disponibles</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Precio */}
                      <div className="text-center">
                        <span className="font-medium text-infinity-yellow">
                          {formatPrice(item.product.price)}
                        </span>
                      </div>

                      {/* Cantidad */}
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                            className="p-1 rounded-full hover:bg-gray-100"
                            disabled={item.quantity <= 1}
                          >
                            <Minus size={16} />
                          </button>
                          <span className="w-12 text-center font-medium">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                            className="p-1 rounded-full hover:bg-gray-100"
                            disabled={typeof item.product.inStock === 'number' && item.quantity >= item.product.inStock}
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      </div>

                      {/* Subtotal */}
                      <div className="text-center">
                        <span className="font-semibold text-infinity-yellow">
                          {formatPrice(item.product.price * item.quantity)}
                        </span>
                      </div>

                      {/* Acciones */}
                      <div className="text-center">
                        <button
                          onClick={() => removeItem(item.product.id)}
                          className="p-2 text-red-500 hover:bg-infinity-yellow/20 rounded-full transition-colors"
                          title="Eliminar del carrito"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="bg-infinity-black px-6 py-4 border-t border-infinity-yellow">
                <div className="flex justify-between items-center">
                  <button
                    onClick={clearCart}
                    className="text-red-600 hover:text-red-800 font-medium"
                  >
                    Vaciar carrito
                  </button>
                  <Link
                    to="/productos"
                    className="text-infinity-yellow hover:text-infinity-white font-medium"
                  >
                    Continuar comprando
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Resumen del Pedido */}
          <div className="lg:col-span-1">
            <div className="bg-infinity-dark rounded-lg shadow-lg p-6 sticky top-4">
              <h2 className="text-xl font-bold text-infinity-yellow mb-6">Resumen del Pedido</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-infinity-white/80">Subtotal</span>
                  <span className="font-medium text-infinity-yellow">{formatPrice(total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-infinity-white/80">Envío</span>
                  <span className="font-medium text-green-400">Gratis</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-infinity-white/80">IGV (18%)</span>
                  <span className="font-medium text-infinity-yellow">{formatPrice(total * 0.18)}</span>
                </div>
                <hr className="border-infinity-yellow/30" />
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-infinity-white">Total</span>
                  <span className="text-infinity-yellow">{formatPrice(total * 1.18)}</span>
                </div>
              </div>

              <Link
                to="/checkout"
                className="w-full btn-primary flex items-center justify-center space-x-2 text-lg py-4 bg-infinity-yellow text-infinity-black hover:bg-infinity-purple hover:text-infinity-white"
              >
                <span>Proceder al Pago</span>
                <ArrowRight size={20} />
              </Link>

              <div className="mt-6 text-center text-sm text-infinity-white/60">
                <p>✓ Pago 100% seguro</p>
                <p>✓ Envío gratis en Lima</p>
                <p>✓ Garantía de satisfacción</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;